import { useEffect } from "react";
import { FormControlLabel, MenuItem, Stack, Switch, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useField } from "formik";

export type MpegTimestampsMode = "original" | "restamp" | "regenerate";
export type KlvTimestampsMode = "original" | "restamp" | "currentTime";

export type FileInputParamsFormValues = {
  loop: boolean;
  mpegTimestampsMode: MpegTimestampsMode;
  klvTimestampsMode: KlvTimestampsMode;
  klvStartTime: string | null;
};

export const createDefaultFileInputParams = (): FileInputParamsFormValues => ({
  loop: false,
  mpegTimestampsMode: "original",
  klvTimestampsMode: "original",
  klvStartTime: null
});

type FileInputParamsProps = {
  name?: string;
  mode?: "view" | "edit";
};

const FileInputParams = ({ name = "file", mode = "edit" }: FileInputParamsProps) => {
  const readOnly = mode === "view";

  const [loopField, , loopHelpers] = useField<boolean>({ name: `${name}.loop` });
  const [mpegModeField, mpegModeMeta, mpegModeHelpers] = useField<MpegTimestampsMode>({
    name: `${name}.mpegTimestampsMode`
  });
  const [klvModeField, klvModeMeta, klvModeHelpers] = useField<KlvTimestampsMode>({
    name: `${name}.klvTimestampsMode`
  });
  const [klvStartTimeField, klvStartTimeMeta, klvStartTimeHelpers] = useField<string | null>({
    name: `${name}.klvStartTime`
  });

  useEffect(() => {
    if (readOnly) {
      return;
    }
    if (klvModeField.value !== "restamp" && klvStartTimeField.value) {
      klvStartTimeHelpers.setValue(null);
    }
  }, [readOnly, klvModeField.value, klvStartTimeField.value, klvStartTimeHelpers]);

  const klvStartTimeValue = klvStartTimeField.value ? dayjs(klvStartTimeField.value) : null;
  const klvStartTimeMissing = klvModeField.value === "restamp" && !klvStartTimeField.value;
  const klvStartTimeErrorText =
    klvStartTimeMeta.touched && klvStartTimeMeta.error
      ? klvStartTimeMeta.error
      : klvStartTimeMissing
        ? "KLV Start Time is required when KLV timestamps mode is Restamp"
        : undefined;

  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(loopField.value)}
            onChange={(event) => loopHelpers.setValue(event.target.checked)}
            name={loopField.name}
            disabled={readOnly}
          />
        }
        label="Loop"
        sx={{ m: 0 }}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          {...mpegModeField}
          label="MPEG timestamps"
          select
          value={mpegModeField.value}
          onChange={(event) => mpegModeHelpers.setValue(event.target.value as MpegTimestampsMode)}
          error={Boolean(mpegModeMeta.touched && mpegModeMeta.error)}
          helperText={mpegModeMeta.touched ? mpegModeMeta.error : undefined}
          disabled={readOnly}
          fullWidth
        >
          <MenuItem value="original">Original</MenuItem>
          <MenuItem value="restamp">Restamp</MenuItem>
          <MenuItem value="regenerate">Regenerate</MenuItem>
        </TextField>
        <TextField
          {...klvModeField}
          label="KLV timestamps"
          select
          value={klvModeField.value}
          onChange={(event) => klvModeHelpers.setValue(event.target.value as KlvTimestampsMode)}
          error={Boolean(klvModeMeta.touched && klvModeMeta.error)}
          helperText={klvModeMeta.touched ? klvModeMeta.error : undefined}
          disabled={readOnly}
          fullWidth
        >
          <MenuItem value="original">Original</MenuItem>
          <MenuItem value="restamp">Restamp</MenuItem>
          <MenuItem value="currentTime">Current time</MenuItem>
        </TextField>
      </Stack>
      {klvModeField.value === "restamp" ? (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label="KLV Start Time"
            value={klvStartTimeValue}
            onChange={(newValue) => {
              klvStartTimeHelpers.setValue(newValue ? newValue.toDate().toISOString() : null);
            }}
            views={["year", "month", "day", "hours", "minutes", "seconds"]}
            format="YYYY-MM-DD HH:mm:ss"
            disabled={readOnly}
            slotProps={{
              textField: {
                fullWidth: true,
                error: Boolean(klvStartTimeErrorText),
                helperText: klvStartTimeErrorText
              }
            }}
          />
        </LocalizationProvider>
      ) : null}
    </Stack>
  );
};

export default FileInputParams;
